import { type SupportedMimeTypes, VIDEO_THUMBNAIL_MAX_TIME_LOCATION } from '@proton/shared/lib/drive/constants';

import { logError } from '../../../../utils/errorHandling';
import { canvasToThumbnail } from './canvasUtil';
import type { Media, ThumbnailInfo } from './interface';
import { ThumbnailType } from './interface';
import { calculateThumbnailSize } from './util';

type ListenerContainer = readonly {
    type: keyof HTMLVideoElementEventMap;
    listener: (event?: Event) => void;
}[];

interface Props extends Media {
    thumbnails?: ThumbnailInfo[];
}

// Creating video thumbnails can be resource heavy operation. Especially if we load multiple, high-res videos.
// Another risk factor is memory leakage. Video elements should be cleared carefully after using them.
// The video should not be added to the DOM - the last thing we want here is displaying them.
// thumbnailType is not actually use for video, we always use preview
export const getVideoInfo = async (file: Blob, mimeType: SupportedMimeTypes.webp | SupportedMimeTypes.jpg) => {
    const promiseInit = (resolve: (value: Props | undefined) => void, reject: (reason?: string) => void) => {
        const video: HTMLVideoElement = document.createElement('video') as HTMLVideoElement;
        const objectUrl = URL.createObjectURL(file);
        video.src = objectUrl;
        video.autoplay = false;
        video.autofocus = false;
        video.defaultMuted = true;
        video.muted = true;
        // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/video#preload
        video.preload = 'auto';

        const destruct = (result: Props | undefined, listeners: ListenerContainer): void => {
            resolve(result);
            listeners.forEach((pair) =>
                video.removeEventListener<keyof HTMLVideoElementEventMap>(pair.type, pair.listener)
            );

            video.src = '';
            URL.revokeObjectURL(objectUrl);
        };

        const listeners: ListenerContainer = [
            {
                type: 'loadedmetadata',
                listener: () => {
                    video.currentTime = Math.min(VIDEO_THUMBNAIL_MAX_TIME_LOCATION, video.duration / 10);
                },
            },
            {
                type: 'seeked',
                listener: async () => {
                    if (video.currentTime === 0) {
                        // To test if we were able to create a thumbnail, we have to check if the browser
                        // was able to play the video
                        // An alternative way of checking would be using !video.canPlayType(file.type),
                        // but according to the documentation it provides only an educated guess.
                        // https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/canPlayType
                        // Instead I start the playback and pause it immediately. In case there was an error,
                        // we know for sure the media was not playable.
                        video
                            .play()
                            .then(() => video.pause())
                            .catch(() => destruct(undefined, listeners));
                        return;
                    }

                    try {
                        await video.play();

                        // Wait for a frame to actually render using timeupdate or a small delay
                        await new Promise((resolve) => {
                            const onFrame = () => {
                                video.removeEventListener('timeupdate', onFrame);
                                resolve(undefined);
                            };
                            video.addEventListener('timeupdate', onFrame);
                            setTimeout(() => {
                                video.removeEventListener('timeupdate', onFrame);
                                resolve(undefined);
                            }, 200);
                        });

                        video.pause();
                    } catch (e) {
                        logError(e);
                    }
                    const canvas: HTMLCanvasElement = document.createElement('canvas');
                    const [width, height] = calculateThumbnailSize({
                        width: video.videoWidth,
                        height: video.videoHeight,
                    });

                    canvas.width = width;
                    canvas.height = height;
                    const context = canvas.getContext('2d', { willReadFrequently: true });

                    if (!context) {
                        reject("Couldn't create canvas context for video thumbnail creation.");
                        return;
                    }

                    try {
                        context.drawImage(video, 0, 0, width, height);

                        destruct(
                            {
                                width: video.videoWidth,
                                height: video.videoHeight,
                                duration: video.duration,
                                thumbnails: [
                                    {
                                        thumbnailData: new Uint8Array(
                                            await canvasToThumbnail(canvas, ThumbnailType.PREVIEW, mimeType)
                                        ),
                                        thumbnailType: ThumbnailType.PREVIEW,
                                    },
                                ],
                            },
                            listeners
                        );
                    } catch (e) {
                        logError(e);
                        destruct(undefined, listeners);
                    }
                },
            },
            { type: 'error', listener: () => destruct(undefined, listeners) },
        ];

        listeners.forEach((pair) => video.addEventListener<keyof HTMLVideoElementEventMap>(pair.type, pair.listener));

        // https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/load
        video.load();
    };

    return new Promise<Props | undefined>(promiseInit);
};
